import React, { useState } from 'react';
import { ExternalLink, Copy, Check, Smartphone } from 'lucide-react';
import { crmUser } from '../crm/api';
import { publicOrigin, isLoopback } from './../crm/publicUrl';

// The rep's own digital card. profileSlug is minted by the vcard server when
// the employee is created and stored on their CRM login.
export default function EmployeeCard() {
  const me = crmUser() || {};
  const slug = me.profileSlug;
  const [copied, setCopied] = useState('');

  const origin = publicOrigin();
  const links = slug ? [
    { key: 'card', icon: Smartphone, title: 'Digital card', url: `${origin}/profile/${slug}`,
      blurb: 'What opens when a prospect taps your NFC card or scans your QR.' },
  ] : [];

  const copy = async (k, url) => {
    try { await navigator.clipboard.writeText(url); setCopied(k); setTimeout(() => setCopied(''), 1800); }
    catch { setCopied(''); }
  };

  if (!slug) {
    return (
      <div className="max-w-[720px] pt-1">
        <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-8 text-center">
          <div className="text-[15px] font-bold text-[var(--text)]">No card yet</div>
          <p className="text-[13px] text-[var(--muted)] mt-1.5 max-w-[46ch] mx-auto">
            Your login isn't linked to a card profile. Ask your admin to set your profile slug on the
            Employees page — everything on this screen appears once it's set.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[900px] pt-1">
      <div>
        <h2 className="text-[20px] font-extrabold text-[var(--heading)] tracking-tight">My digital card</h2>
        <p className="text-[13px] text-[var(--muted)]">Share it anywhere. Every enquiry from it lands on your name.</p>
      </div>
      {isLoopback() && (
        <div className="text-[12.5px] rounded-lg p-2.5" style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' }}>
          <b>This link only works on this computer.</b> It points at “localhost”, which on a phone means the phone itself.
          Ask your admin to set the app's public address before you flash this onto a card.
        </div>
      )}

      <div className="grid grid-cols-1 max-w-[520px]">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <div key={l.key} className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-6 flex flex-col gap-3">
              <div className="flex items-center gap-2.5">
                <Icon size={19} style={{ color: 'var(--acc2)' }} />
                <h3 className="font-extrabold text-[var(--text)] text-[16px]">{l.title}</h3>
              </div>
              <p className="text-[12.5px] text-[var(--muted)] leading-relaxed">{l.blurb}</p>
              <div className="text-[11.5px] font-mono break-all rounded-lg px-3 py-2" style={{ background: 'var(--soft)', color: 'var(--muted)' }}>{l.url}</div>
              <div className="flex gap-2 mt-auto pt-1">
                <a href={l.url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1.5 acc-bg text-white text-[13px] font-bold px-3.5 py-2 rounded-lg">
                  <ExternalLink size={14} /> Open
                </a>
                <button onClick={() => copy(l.key, l.url)}
                  className="flex items-center gap-1.5 text-[13px] font-bold px-3.5 py-2 rounded-lg border border-[var(--border)] text-[var(--text)] hover:bg-[var(--soft)]">
                  {copied === l.key ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy link</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-[var(--card)] rounded-3xl border border-[var(--border)] card-lux p-6">
        <h3 className="font-extrabold text-[var(--text)] text-[16px] mb-3">How a tap becomes your lead</h3>
        <ol className="space-y-2.5">
          {[
            'Tap your NFC card on the prospect\'s phone — or let them scan your QR.',
            'Your digital card opens in their browser. No app, nothing to install.',
            'They tap through to your microsite and submit Contact Me, Get Quote or WhatsApp.',
            'The lead is created, assigned to you, and you get notified — you type nothing.',
          ].map((s, i) => (
            <li key={i} className="flex gap-3 text-[13px] text-[var(--muted)]">
              <span className="w-5 h-5 rounded-full shrink-0 flex items-center justify-center text-[10px] font-black text-white" style={{ background: 'var(--acc)' }}>{i + 1}</span>
              <span className="leading-relaxed">{s}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
